// components/layout/OfflineBanner.tsx — Shows when user is offline or syncing

import { useState, useEffect } from 'react';
import { useOffline } from '@/hooks/useOffline';
import styles from './OfflineBanner.module.css';
import clsx from 'clsx';

export function OfflineBanner() {
  const { isOnline, isSyncing, pendingCount } = useOffline();
  const [wasOffline, setWasOffline] = useState(false);
  const [hideTimer, setHideTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      if (hideTimer) clearTimeout(hideTimer);
    } else if (wasOffline) {
      // Show "Back online" briefly then hide
      const timer = setTimeout(() => setWasOffline(false), 3000);
      setHideTimer(timer);
    }
    return () => {
      if (hideTimer) clearTimeout(hideTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // Don't render if always been online
  if (isOnline && !wasOffline && !isSyncing) return null;

  let message = 'Offline · Viewing cached data';
  if (isOnline && isSyncing) {
    message = pendingCount > 0
      ? `Syncing ${pendingCount} change${pendingCount > 1 ? 's' : ''}…`
      : 'Syncing…';
  } else if (isOnline && wasOffline) {
    message = '✓ Back online';
  }

  return (
    <div
      className={clsx(
        styles.banner,
        isOnline ? (isSyncing ? styles.syncing : styles.backOnline) : styles.offline
      )}
      role="status"
      aria-live="polite"
    >
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.text}>{message}</span>
    </div>
  );
}
